import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { DataService } from 'src/services/data-service';

interface SecurityDepositCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Lease {
  id: string;
  refNumber: string;
  status: string;
  tenant: {
    fullName: string;
  } | null;
  unit: {
    number: string;
  } | null;
}

const SecurityDepositCreateDialog: React.FC<SecurityDepositCreateDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [loadingLeases, setLoadingLeases] = useState(false);
  const [formData, setFormData] = useState({
    leaseId: '',
    amount: 0,
    paymentDate: new Date().toISOString().slice(0, 16)
  });

  useEffect(() => {
    if (open) {
      fetchLeases();
    }
  }, [open]);

  const fetchLeases = async () => {
    setLoadingLeases(true);
    try {
      const response = await DataService.get('api/Lease/all');

      if (response.ok) {
        const responseData = await response.json();
        let leasesList: Lease[] = [];

        if (responseData.success && responseData.data) {
          leasesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          leasesList = responseData;
        }

        // Filter only active leases
        leasesList = leasesList.filter(lease => lease.status === 'Active' || lease.status === 'Draft');

        setLeases(leasesList);
      } else {
        throw new Error('Failed to fetch leases');
      }
    } catch (error) {
      console.error('Error fetching leases:', error);
      setError('Failed to load leases. Please try again.');
    } finally {
      setLoadingLeases(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!formData.leaseId) {
      setError('Please select a lease');
      return;
    }

    if (formData.amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    if (!formData.paymentDate) {
      setError('Please select a payment date');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        leaseId: formData.leaseId,
        amount: Number(formData.amount),
        paymentDate: new Date(formData.paymentDate).toISOString()
      };

      console.log('Submitting security deposit:', payload);

      const response = await DataService.post('api/SecurityDeposit/create', payload);

      if (response.ok) {
        onSuccess();
        handleClose();
      } else {
        let errorMessage = `Failed to create security deposit (${response.status})`;
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          errorMessage = errorData.message || errorData.title || JSON.stringify(errorData);
        } catch (e) {
          errorMessage = `API Error: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
        console.error('Security deposit creation failed:', errorMessage, 'Response:', response);
      }
    } catch (err) {
      console.error('Error creating security deposit:', err);
      setError('An error occurred while creating the security deposit');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      leaseId: '',
      amount: 0,
      paymentDate: new Date().toISOString().slice(0, 16)
    });
    setError(null);
    onClose();
  };

  const selectedLease = leases.find(l => l.id === formData.leaseId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'info.main', color: 'white' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AccountBalanceWalletIcon />
            <Typography variant="h6" fontWeight="bold">
              Add Security Deposit
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              options={leases}
              getOptionLabel={(option: Lease) => {
                const parts = [option.refNumber || option.id.slice(0, 8)];
                if (option.tenant?.fullName) parts.push(option.tenant.fullName);
                if (option.unit?.number) parts.push(`Unit ${option.unit.number}`);
                parts.push(`(${option.status})`);
                return parts.join(' - ');
              }}
              value={selectedLease || null}
              onChange={(_, newValue: Lease | null) => {
                handleInputChange('leaseId', newValue?.id || '');
              }}
              loading={loadingLeases}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Lease"
                  required
                  helperText="Choose the lease for this security deposit"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingLeases ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="600">
                      {option.refNumber || option.id.slice(0, 8)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.tenant?.fullName || 'No tenant'} • Unit {option.unit?.number || 'N/A'} • {option.status}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Deposit Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              required
              helperText="Enter the security deposit amount"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Payment Date & Time"
              type="datetime-local"
              value={formData.paymentDate}
              onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              required
              helperText="When was the deposit paid?"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || formData.amount <= 0 || !formData.leaseId}
          startIcon={loading ? <CircularProgress size={20} /> : <AccountBalanceWalletIcon />}
        >
          {loading ? 'Adding...' : 'Add Security Deposit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SecurityDepositCreateDialog;
